import type { RoadmapLevelStage } from 'Selectors/curriculum';

export type Point = { x: number; y: number };
export const LEVEL_SPACING = 190;
export const WORLD_PADDING = 180;
/** Room above the highest lesson for its single-line name. */
export const LABEL_CLEARANCE = 84;
const POD_WIDTH = 190;
const STAGE_MARGIN = 24;
const BOTTOM_MARGIN = 16;
const COMPACT_MIN_WIDTH = 320;
/** Projected platform height per unit of width for the full and compact models. */
const PLATFORM_ASPECT = { full: 0.44, compact: 0.82 };

export type StagePosition = Point & { width: number; height: number };

/**
 * Every open platform owns space in the world, including densely assigned curricula.
 * The path and platforms are fitted to the viewport height before the map scrolls vertically.
 */
export function journeyLayout(
  graph: RoadmapLevelStage[],
  viewport: { width: number; height: number },
  openStages: Set<number>,
) {
  const narrow = viewport.width < 600;
  const short = viewport.height < 280;
  const amplitude = short
    ? 16
    : narrow
      ? 28
      : Math.round(Math.min(65, Math.max(34, viewport.height * 0.1)));
  const pathTop = short ? 64 : LABEL_CLEARANCE;
  const heightBelow = (bridge: number) =>
    viewport.height - (pathTop + 2 * amplitude + bridge) - BOTTOM_MARGIN;
  // Phones use the deeper 2 × 2 platform only when it fits; otherwise a single row.
  const compact =
    narrow && !short && heightBelow(55) >= COMPACT_MIN_WIDTH * PLATFORM_ASPECT.compact;
  const bridge = compact ? 55 : 48;
  const aspect = compact ? PLATFORM_ASPECT.compact : PLATFORM_ASPECT.full;
  const available = heightBelow(bridge);
  const platformWidth = Math.round(
    Math.min(
      short ? 380 : compact ? 360 : 680,
      Math.max(280, viewport.width - 40),
      short ? Infinity : Math.max(narrow ? COMPACT_MIN_WIDTH : 420, available / aspect),
    ),
  );
  const platformHeight = short
    ? Math.min(184, Math.max(150, viewport.height - 28))
    : Math.round(platformWidth * aspect);
  // Spare height is shared above the labels and below the platforms.
  const baseline = pathTop + amplitude + Math.max(0, available - platformHeight) / 2;
  const dockY = baseline + amplitude + bridge;

  // Reserve space between every branch, distributing it across the intervening lessons.
  // An open stage must not stretch every neighbouring lesson into a separate card.
  const branchIndexes = graph.flatMap((stage, i) => (stage.repertoire ? [i] : []));
  const footprint = (i: number) =>
    openStages.has(graph[i].levelNumber) ? platformWidth : POD_WIDTH;
  const gaps = graph.map((_, i) => {
    const next = branchIndexes.findIndex((branch) => branch >= i);
    if (!i || next <= 0) return LEVEL_SPACING;
    const [from, to] = [branchIndexes[next - 1], branchIndexes[next]];
    return Math.max(LEVEL_SPACING, ((footprint(from) + footprint(to)) / 2 + 52) / (to - from));
  });
  const offsets = gaps.reduce<number[]>((all, gap, i) => [...all, i ? all[i - 1] + gap : 0], []);
  const span = offsets[offsets.length - 1] ?? 0;
  const halfStage = (i: number) =>
    openStages.has(graph[i].levelNumber) ? platformWidth / 2 + STAGE_MARGIN : 0;
  const left = Math.max(WORLD_PADDING, ...graph.map((_, i) => halfStage(i) - offsets[i]));
  const right = Math.max(WORLD_PADDING, ...graph.map((_, i) => halfStage(i) - (span - offsets[i])));
  const points = offsets.map((offset, i) => ({
    x: left + offset,
    y: baseline + Math.sin(i * 1.05) * amplitude,
  }));
  // A shared dock line keeps bridges clear of both the path and adjacent platforms.
  const platforms = new Map<number, StagePosition>(
    graph.flatMap((stage, i) =>
      openStages.has(stage.levelNumber)
        ? [
            [
              stage.levelNumber,
              {
                x: points[i].x - platformWidth / 2,
                y: dockY,
                width: platformWidth,
                height: platformHeight,
              },
            ] as const,
          ]
        : [],
    ),
  );
  const lowestNode = baseline + amplitude;
  return {
    points,
    platforms,
    width: Math.max(viewport.width, left + span + right),
    height: Math.max(
      viewport.height,
      platforms.size ? dockY + platformHeight + BOTTOM_MARGIN : lowestNode + 240,
    ),
    short,
    compact,
  };
}

export function ribbonPath(points: Point[]): string {
  if (!points.length) return '';
  return points.slice(1).reduce((path, p, i) => {
    const previous = points[i];
    const middle = (previous.x + p.x) / 2;
    return `${path} C ${middle} ${previous.y}, ${middle} ${p.y}, ${p.x} ${p.y}`;
  }, `M ${points[0].x} ${points[0].y}`);
}

/** Overview row turns bow outward past the repertoire tags and names at each row end. */
const overviewTurn = (width: number) =>
  width < 520 ? { margin: 60, bow: 68 } : { margin: 84, bow: 96 };

export function overviewPath(points: Point[], width: number): string {
  if (!points.length) return '';
  const { bow } = overviewTurn(width);
  return points.slice(1).reduce((path, p, i) => {
    const previous = points[i];
    if (Math.abs(previous.x - p.x) > 1) {
      const middle = (previous.x + p.x) / 2;
      return `${path} C ${middle} ${previous.y}, ${middle} ${p.y}, ${p.x} ${p.y}`;
    }
    const direction = points[i - 1] && points[i - 1].x > previous.x ? -1 : 1;
    const x = previous.x + direction * bow;
    return `${path} C ${x} ${previous.y}, ${x} ${p.y}, ${p.x} ${p.y}`;
  }, `M ${points[0].x} ${points[0].y}`);
}

export function overviewPoints(count: number, width: number) {
  const { margin } = overviewTurn(width);
  const columns = Math.max(3, Math.floor(width / 130));
  const spacing = (width - 2 * margin) / Math.max(1, columns - 1);
  return Array.from({ length: count }, (_, i) => {
    const row = Math.floor(i / columns);
    const column = i % columns;
    return {
      x: margin + (row % 2 ? columns - 1 - column : column) * spacing,
      y: 60 + row * 135,
    };
  });
}
